import { useState } from 'react';
import { useAppDispatch } from '@store/hooks';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Form, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { useMemo } from 'react';

import Breadcrumb from '@components/Shared/Breadcrumb';
import useGetGlJeCategoryList from '@hooks/Finance/useGetGlJeCategoryList';
import useGetGlJeSourceList from '@hooks/Finance/useGetGlJeSourceList';
import useGetGlPeriodsList from '@hooks/Finance/GlPeriods/useGetGlPeriodsList';
import useLookupCurrency from '@hooks/Lookups/use-Lookup-Currency';
import { useNavigate, useParams } from 'react-router-dom';
import { type GlJeLinesList } from '@store/Finance/Journals/JournalsModels';
import { UpdatGlJeHeaders } from '@store/Finance/Journals/JournalsSlice';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import SwalAlert from '@components/Shared/Alert/SwalAlert';
import useGetJournalsById from '@hooks/Finance/Journals/useGetJournalsById';
import { Card, CardBody } from '@components/card/card';
import JournalLines2 from '../JournalLines/JournalLines2';
import useCheckDateOnPeriod from '@hooks/Finance/GlPeriods/useCheckDateOnPeriod';
import * as Yup from 'yup';

import Loading from '@components/Shared/Loader/Loading';
import useGlChartLookups from '@hooks/Finance/ChartAccountsDetails/useGlChartLookups';
import useGetGlLedgersList from '@hooks/Finance/useGetGlLedgersList';

const emptyToUndef = (value: any, originalValue: any) => {
  // يسمح بالفارغ في الـ UI لكن يعتبره "غير مُدخل" في الفالديشن
  if (originalValue === '' || originalValue === null || originalValue === undefined) return undefined;
  const n = Number(originalValue);
  return Number.isNaN(n) ? undefined : n;
};

const AddGlJeHeaderSchema = (t: any) => {
  const requiredMsg = () => t('Required');

  const amountSchema = Yup.number().transform(emptyToUndef).typeError(requiredMsg()).min(0, requiredMsg());

  const lineSchema = Yup.object()
    .shape({
      lineType: Yup.string().oneOf(['credit', 'debit']).required(requiredMsg()),

      description: Yup.string().trim().required(requiredMsg()),

      accountId: Yup.string().nullable().required(requiredMsg()),

      enteredCr: amountSchema.when('lineType', {
        is: 'credit',
        then: s => s.required(requiredMsg()).moreThan(0, requiredMsg()),
        otherwise: s => s.test('credit-must-be-zero', requiredMsg(), v => v === undefined || Number(v) === 0)
      }),

      enteredDr: amountSchema.when('lineType', {
        is: 'debit',
        then: s => s.required(requiredMsg()).moreThan(0, requiredMsg()),
        otherwise: s => s.test('debit-must-be-zero', requiredMsg(), v => v === undefined || Number(v) === 0)
      })
    })
    .test('only-one-side', requiredMsg(), (line: any) => {
      // يمنع إدخال مدين ودائن معًا في نفس السطر
      const cr = Number(line?.enteredCr || 0);
      const dr = Number(line?.enteredDr || 0);
      return !(cr > 0 && dr > 0);
    });

  return Yup.object().shape({
    glDate: Yup.string().required(requiredMsg()),

    description: Yup.string().trim().required(requiredMsg()),

    jeSource: Yup.string().notOneOf(['إختر', 'Choose'], 'Required').required(requiredMsg()),
    jeCategory: Yup.string().notOneOf(['إختر', 'Choose'], 'Required').required(requiredMsg()),

    periodId: Yup.string().notOneOf(['إختر', 'Choose'], 'Required').required(requiredMsg()),

    currencyCode: Yup.string().notOneOf(['إختر', 'Choose'], 'Required').required(requiredMsg()),

    // ✅ Validation للسطور
    JeLinesList: Yup.array().of(lineSchema).min(1, i18nFallback(t, 'Required')) // أو رسالة خاصة مثل: "يجب إضافة سطر واحد على الأقل"
  });
};

function i18nFallback(t: any, key: string) {
  try {
    return t(key);
  } catch {
    return key;
  }
}

const UpdateJournals = () => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [stateJu, setStateJu] = useState<string>('T');
  const { Id } = useParams();
  const dispatch = useAppDispatch();

  const { JournalsById: data, loading, error } = useGetJournalsById(Id);
  const { Currency, loading: loading1, error: error1 } = useLookupCurrency();
  const { GlJeCategoryList, loading: loading2, error: error2 } = useGetGlJeCategoryList();
  const { GlJeSourceList, loading: loading3, error: error3, OptionsJeSource } = useGetGlJeSourceList();

  const { LedgersList, OptionsLedgers, loading: loading6, error: error6 } = useGetGlLedgersList();

  const { GlPeriods, loading: loading4, error: error4 } = useGetGlPeriodsList();

  // console.log(data);

  const {
    OptionsCostCenters,
    OptionsCompanies,
    OptionsBranches,
    OptionsAccounts,
    loading: loading5,
    error: error5
  } = useGlChartLookups();

  const isPosted = data?.status === 'P';

  const initialValues = useMemo(() => {
    return {
      id: data?.id ?? '',
      glDate: data?.glDate ?? '',
      ledgerId: data?.ledgerId ?? '',
      jeCategory: data?.jeCategory ?? '',
      jeSource: data?.jeSource ?? '',
      periodId: data?.periodId ?? '',
      name: data?.name ?? '',
      status: data?.status ?? '',
      currencyCode: data?.currencyCode ?? '',
      conversionFlag: data?.conversionFlag ?? false,
      currencyConversionRate: data?.currencyConversionRate ?? '',
      conversionDate: data?.conversionDate ?? '',
      jeBatchId: data?.jeBatchId ?? '',
      parentJeHeaderId: data?.parentJeHeaderId ?? null,
      postedDate: data?.postedDate ?? '',
      description: data?.description ?? '',
      accountedDr: data?.accountedDr ?? '',
      accountedCr: data?.accountedCr ?? '',
      JeLinesList:
        data?.glJeLinesList?.map(item => ({
          id: item.id,
          jeLineNum: item.jeLineNum,
          enteredDr: item.enteredDr,
          enteredCr: item.enteredCr,
          accountDr: item.accountDr,
          accountCr: item.accountCr,
          description: item.description,
          accountId: item.accountId,
          companyId: item.companyId,
          branchId: item.branchId,
          costCenterId: item.costCenterId,
          lineType: item.enteredCr === 0 ? 'debit' : 'credit'
          // ❌ لا تحسب codeCombination هنا
        })) ?? []
    };
  }, [data?.id]); // أو [data] لو data ثابتة



  //#region Formik
  const formik = useFormik({
    // initialValues: {
    //   id: data ? data?.id : '',
    //   glDate: data ? data?.glDate : '',
    //   ledgerId: data ? data?.ledgerId : '',
    //   jeCategory: data ? data?.jeCategory : '',
    //   jeSource: data ? data?.jeSource : '',
    //   periodId: data ? data?.periodId : '',
    //   name: data ? data?.name : '',
    //   status: data ? data?.status : '',
    //   //تحويلات العملة
    //   currencyCode: data ? data?.currencyCode : '',
    //   conversionFlag: data ? data?.conversionFlag : false,
    //   currencyConversionRate: data ? data?.currencyConversionRate : '',
    //   conversionDate: data ? data?.conversionDate : '',
    //   ///
    //   jeBatchId: data ? data?.jeBatchId : '',
    //   parentJeHeaderId: data ? data?.parentJeHeaderId : '',
    //   postedDate: data ? data?.postedDate : '',
    //   description: data ? data?.description : '', // الوصف - البيان
    //   accountedDr: data ? data?.accountedDr : '',
    //   accountedCr: data ? data?.accountedCr : '',

    //   // JeLinesList: [] as GlJeLinesList[],
    //   JeLinesList:
    //     data?.glJeLinesList?.map(item => ({
    //       id: item.id,
    //       jeLineNum: item.jeLineNum,
    //       enteredDr: item.enteredDr,
    //       enteredCr: item.enteredCr,
    //       accountDr: item.accountDr,
    //       accountCr: item.accountCr,
    //       description: item.description,
    //       accountId: item.accountId,
    //       companyId: item.companyId,
    //       branchId: item.branchId,
    //       costCenterId: item.costCenterId,
    //       //________للعرض وليس للبوست_______
    //       lineType: item.enteredCr === 0 ? 'debit' : 'credit',
    //       codeCombination: [
    //         OptionsCompanies.find(f => f.value === item.companyId)?.accountNumber,
    //         OptionsBranches.find(f => f.value === item.branchId)?.accountNumber,
    //         OptionsCostCenters.find(f => f.value === item.costCenterId)?.accountNumber,
    //         OptionsAccounts.find(f => f.value === item.accountId)?.accountNumber
    //       ]
    //         .filter(Boolean)
    //         .join('  -  ')
    //     })) ?? ([] as GlJeLinesList[])
    // },
     initialValues,
    enableReinitialize: true,
    validationSchema: AddGlJeHeaderSchema(t),
    onSubmit: values => {
      if (isPosted) {
        SwalAlert({ text: 'هذا القيد مُرحّل (Posted) ولا يمكن تعديله.' });
        formik.setSubmitting(false);
        return;
      }

      if (!isDateValid) {
        SwalAlert({ text: 'التاريـخ ليس ضمن الفترة المحاسبية' });
        formik.setSubmitting(false);
        return;
      }

      if (stateJu === 'U' && Number(totalEnteredDr) !== Number(totalEnteredCr)) {
        SwalAlert({ text: 'يجب ان يتساوى الدائن والمديـن' });
        formik.setSubmitting(false);
        return;
      }

      dispatch(
        UpdatGlJeHeaders({
          id: values.id,
          glDate: values.glDate,
          ledgerId: values.ledgerId,
          jeCategory: values.jeCategory,
          jeSource: values.jeSource,
          periodId: values.periodId,
          name: values.name,
          status: stateJu,
          currencyCode: values.currencyCode,
          conversionFlag: values.conversionFlag,
          currencyConversionRate: 1,
          // conversionDate: values.conversionDate,
          conversionDate: null,
          ///
          // jeBatchId: values.jeBatchId,
          // parentJeHeaderId: values.parentJeHeaderId,
          jeBatchId: null,
          parentJeHeaderId: null,
          // postedDate: values.postedDate,
          postedDate: new Date().toISOString().split('T')[0],
          description: values.description,

          accountedDr: Number(totalAccountedDr),
          accountedCr: Number(totalAccountedCr),
          enteredDr: Number(totalEnteredDr),
          enteredCr: Number(totalEnteredCr),

          glJeLinesList: values.JeLinesList?.map(item => ({
            id: item.id,
            jeLineNum: Number(item.jeLineNum),
            enteredDr: Number(item.enteredDr) || 0,
            enteredCr: Number(item.enteredCr) || 0,
            accountDr: Number(item.accountDr) || 0,
            accountCr: Number(item.accountCr) || 0,
            description: item.description,
            accountId: item.accountId,
            companyId: item.companyId,
            branchId: item.branchId,
            costCenterId: item.costCenterId
          }))
        })
      )
        .unwrap()
        .then((res: any) => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Add', msg: 'تم حفظ القيد بنجاح' });
            formik.resetForm();
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });
  const { values, handleChange: formikhandleChange, errors, touched } = formik;

 
  // Calculate the total debit and credit amounts
  const totalEnteredDr = formik.values.JeLinesList.reduce((total, line) => total + line.enteredDr, 0);
  const totalEnteredCr = formik.values.JeLinesList.reduce((total, line) => total + line.enteredCr, 0);
  const totalAccountedDr = formik.values.JeLinesList.reduce((total, line) => total + line.accountDr, 0);
  const totalAccountedCr = formik.values.JeLinesList.reduce((total, line) => total + line.accountCr, 0);

  //#endregion

  // console.log(data.status);

  const fieldNames: Record<string, string> = {
    periodId: 'الفترة المحاسبية',
    jeSource: 'المصدر',
    jeCategory: 'التصنيف',
    ledgerId: 'الأستاذ العام',
    glDate: 'التاريخ',
    description: 'البيان',
    currencyCode: 'العملة'
    // إذا أردت إضافة رسائل للحقول الفرعية داخل السطور (قد تكون معقدة)
    // يمكنك تجاهلها حالياً لأنها ستظهر كـ "JeLinesList" بشكل افتراضي
  };

  const isDateValid = useCheckDateOnPeriod(values.glDate, values.periodId);

  return (
    <div>
      <Breadcrumb
        BreadcrumbList={[
          {
            name: 'Home',
            link: ':moduleHome'
          },
          {
            name: 'القيود المالية',
            link: '/Finance/Journals'
          },
          {
            name: 'تعديـل القيد',
            link: null
          }
        ]}
        PageName='تعديـل القيد'
      />
      {isPosted && (
        <Alert variant='danger' className='mb-3'>
          <i className='fas fa-lock me-2' />
          هذا القيد مُرحّل ولا يمكن تعديله.
        </Alert>
      )}
      <Form onSubmit={formik.handleSubmit}>
        <Loading
          loading={loading || loading1 || loading2 || loading3 || loading4 || loading5}
          error={error || error1 || error2 || error3 || error4 || error5}
        >
          <Card className='shadow-sm'>
            <CardBody>
              <div className='row'>
                <div className='col-lg-4'>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('الأستاذ العام')}</label>
                    <Select
                      isDisabled
                      classNamePrefix='react-select'
                      isLoading={loading4}
                      isSearchable={true}
                      options={OptionsLedgers}
                      value={OptionsLedgers.find(f => f.value === values.ledgerId)}
                      placeholder={i18n.language === 'ar' ? 'الأستاذ العام المختار' : 'Selected Ledger'}
                    />
                  </div>
                </div>

                <div className='col-lg-4'>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('AccountingPeriod')}</label>
                    <Form.Select
                      disabled={isPosted}
                      className='form-select '
                      name='periodId'
                      onChange={formikhandleChange}
                      value={values.periodId || ''}
                      isInvalid={!!(touched.periodId && errors.periodId)}
                      isValid={formik.touched.periodId && !formik.errors.periodId}
                    >
                      <option value=''>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
                      {GlPeriods &&
                        GlPeriods?.map(item => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>{errors.periodId}</Form.Control.Feedback>
                  </div>
                </div>

                <div className='col-lg-4'>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('Date')}</label>
                    <Form.Control
                      disabled={isPosted}
                      className='form-control  text-center'
                      type='date'
                      name='glDate'
                      onChange={formikhandleChange}
                      value={values.glDate || ''}
                      isInvalid={!!(touched.glDate && errors.glDate)}
                      isValid={formik.touched.glDate && !formik.errors.glDate}
                    />
                    <Form.Control.Feedback type='invalid'>{errors.glDate}</Form.Control.Feedback>
                  </div>
                </div>
                <div className='col-lg-4'>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('Source')}</label>

                    <Select
                      classNamePrefix='react-select'
                      isLoading={loading3}
                      isSearchable={true}
                      isClearable
                      options={OptionsJeSource}
                      onChange={(option: any) => formik.setFieldValue('jeSource', option?.value || '')}
                      value={OptionsJeSource.find(f => f.value === formik.values.jeSource)}
                      placeholder={i18n.language === 'ar' ? 'اختر' : 'Choose'}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </div>
                </div>

                <div className='col-lg-4'>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('Category')}</label>
                    <Form.Select
                      disabled={isPosted}
                      className='form-select '
                      name='jeCategory'
                      onChange={formikhandleChange}
                      value={values.jeCategory || ''}
                      isInvalid={!!(touched.jeCategory && errors.jeCategory)}
                      isValid={formik.touched.jeCategory && !formik.errors.jeCategory}
                    >
                      <option value=''>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
                      {GlJeCategoryList &&
                        GlJeCategoryList?.map(item => (
                          <option key={item.id} value={item.id}>
                            {i18n.language === 'en' ? item.name : item.name2}
                          </option>
                        ))}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>{errors.jeCategory}</Form.Control.Feedback>
                  </div>
                </div>

                <div className='col-lg-2'>
                  <div className='form-group mb-3'>
                    <label className='form-label'> {t('Currency')} </label>
                    <Form.Select
                      disabled
                      className='form-select'
                      name='currencyCode'
                      onChange={formikhandleChange}
                      value={values.currencyCode || ''}
                      isInvalid={!!(touched.currencyCode && errors.currencyCode)}
                      isValid={formik.touched.currencyCode && !formik.errors.currencyCode}
                    >
                      {Currency &&
                        Currency?.map((item: any, idx: number) => (
                          <option key={++idx} value={item.currencyCode}>
                            {item.description}
                          </option>
                        ))}
                    </Form.Select>
                  </div>
                </div>

                <div className='col-lg-2'>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('ExchangeRate')}</label>
                    <Form.Control
                      disabled
                      className='form-control text-center bg-inverse bg-opacity-15'
                      type='text'
                      autoComplete='off'
                      name='currencyConversionRate'
                      value={values.currencyConversionRate}
                    />
                  </div>
                </div>
                <div className='col-lg-12'>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('Note')}</label>
                    <Form.Control
                      className='form-control'
                      as='textarea'
                      rows={1}
                      name='description'
                      onChange={formik.handleChange}
                      value={values.description}
                      isInvalid={!!(touched.description && errors.description)}
                      isValid={touched.description && !errors.description}
                    />
                    <div></div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
          <Card className='mt-3 border-0'>
            <JournalLines2 formik={formik} isUpdate isPosted={isPosted} />
          </Card>

          {Object.keys(formik.errors)?.length > 0 && (
            <div className='alert alert-danger mt-3 border rounded-2 shadow-sm' role='alert'>
              <h6 className='mb-1 text-danger'>
                <i className='bi bi-exclamation-triangle-fill me-2'></i>
                {i18n.language === 'en' ? 'Please review the following fields:' : '  يُرجى مراجعة الحقول التالية:'}
              </h6>
              <ul className='mb-0 ps-4 mt-2'>
                {Object?.entries(formik?.errors)?.map(([field, error]: any) => {
                  // تجاهل أخطاء المصفوفات (مثل JeLinesList) لأنها تحتوي على كائنات
                  if (field === 'JeLinesList' || typeof error === 'object') {
                    return null;
                  }

                  return (
                    <li key={field} className='small'>
                      <strong>{fieldNames[field] ?? field}:</strong> <span className='fw-normal'>{error}</span>
                    </li>
                  );
                })}
              </ul>

              {/* رسالة منفصلة لأخطاء السطور */}
              {formik.errors.JeLinesList && (
                <div className='mt-2 small text-warning'>
                  <i className='bi bi-exclamation-circle me-1'></i>
                  {i18n.language === 'ar'
                    ? 'يوجد خطأ في بيانات السطور - يرجى مراجعة السطور للتأكد من إدخال جميع البيانات المطلوبة بشكل صحيح'
                    : 'There is an error in the journal lines - please review the lines to ensure all required data is entered correctly'}
                </div>
              )}
            </div>
          )}

          <Card className='mt-3'>
            <div className='text-center p-1 '>
              <button
                disabled={isPosted || formik.isSubmitting}
                // disabled={formik.isSubmitting}
                type='button'
                className='btn btn-lg me-1 btn-warning rounded-2 '
                onClick={() => {
                  setStateJu('T');
                  formik.submitForm();
                }}
              >
                <i className='fa fa-save fa-fw me-1'></i>
                {t('TemporarySave')}
              </button>
              <button
                disabled={isPosted || formik.isSubmitting}
                // disabled={formik.isSubmitting}
                type='button'
                className='btn btn-lg me-1 btn-theme rounded-2 '
                onClick={() => {
                  setStateJu('U');
                  formik.submitForm();
                }}
              >
                {formik.isSubmitting ? (
                  <div className='spinner-border spinner-border-sm me-2'></div>
                ) : (
                  <i className='fa fa-save fa-fw me-1'></i>
                )}

                {t('Save')}
              </button>
              <button
                type='button'
                className='btn btn-lg me-1 btn-default   rounded-2'
                onClick={() => navigate('/Finance/Journals', { replace: true })}
              >
                <i className='fa fa-times fa-fw me-1'></i>

                {t('Cancel')}
              </button>
            </div>
          </Card>
        </Loading>
      </Form>
    </div>
  );
};

export default UpdateJournals;
